import React, { ChangeEvent } from 'react';
import { useStore } from '../../../contexts/mobx';
import { Spin, Tooltip } from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    CheckCircleFilled,
} from '@ant-design/icons';
import { NotificationManager } from '../../../components/notification-manager';
import { HttpMethod } from '../../../types';
import * as yup from 'yup';
import style from './IconUploader.module.scss';

const MAX_ICON_SIZE_BYTES = 50_000;

const YupImageUrl = yup.object({
    url: yup.string().required(),
});

interface IconUploaderProps {
    url: string | null | undefined;
    onChange: (url: string | null) => void;
    onDelete: () => void;
}

export const IconUploader = ({
    url,
    onChange,
    onDelete,
}: IconUploaderProps) => {
    const rootStore = useStore();
    const [isUploading, setIsUploading] = React.useState(false);
    const hiddenInputRef = React.useRef<HTMLInputElement | null>(null);

    const onFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (
            !file.name.toLowerCase().endsWith('.svg') ||
            file.type !== 'image/svg+xml'
        ) {
            NotificationManager.showError('Only SVG files are allowed');
            return;
        }

        if (file.size > MAX_ICON_SIZE_BYTES) {
            NotificationManager.showError('Icon file should not exceed 50KB');
            return;
        }

        if (hiddenInputRef.current) {
            hiddenInputRef.current.value = '';
        }

        const formData = new FormData();
        formData.set('folder', 'promotions');
        formData.set('picture', file);

        setIsUploading(true);
        const response = await rootStore.makeNetworkCall(
            {
                url: '/config/picture',
                method: HttpMethod.POST,
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' },
            },
            YupImageUrl
        );
        setIsUploading(false);

        if (!response.err) {
            onChange(response.data.url);
        }
    };

    return (
        <div className={style.icon_uploader}>
            {url ? (
                <div className={style.uploaded_state}>
                    <Tooltip
                        title={
                            <div className={style.icon_preview_bg}>
                                <img
                                    src={url}
                                    alt="icon preview"
                                    className={style.icon_preview_img}
                                />
                            </div>
                        }
                        color="transparent"
                        overlayInnerStyle={{
                            padding: 0,
                            boxShadow: 'none',
                        }}
                    >
                        <div className={style.uploaded_state_label}>
                            <CheckCircleFilled className={style.check_icon} />
                            <span style={{ fontSize: 13 }}>Icon uploaded</span>
                        </div>
                    </Tooltip>
                    <DeleteOutlined
                        className={style.delete_icon}
                        onClick={onDelete}
                    />
                </div>
            ) : (
                <div
                    className={style.upload_button}
                    onClick={() => hiddenInputRef.current?.click()}
                >
                    {isUploading ? <Spin size="small" /> : <PlusOutlined />}
                </div>
            )}
            <input
                type="file"
                accept=".svg"
                style={{ display: 'none' }}
                ref={hiddenInputRef}
                onChange={onFileSelected}
            />
        </div>
    );
};
